import { useEffect, useState } from "react";

import { useWorkspaceSession } from "../../api/workspaceSession";
import { useAuth } from "../../auth/AuthContext";
import {
  Button,
  Modal,
  ModalCloseButton,
  pageCount,
  Pagination,
  PlusIcon,
  Search,
} from "../../components/ui";
import {
  Empty,
  ErrorState,
  InlineNotice,
  Loading,
  PageHeader,
} from "../../components/WorkspacePrimitives";
import { hideRunModelColumns } from "../../lib/featureFlags";
import {
  AGENT_RUN_PAGE_SIZE,
  type AgentRun,
  useAgentRunPage,
} from "../api/e2e";
import AgentRunTable from "../components/AgentRunTable";
import RunCreatorSelect, {
  ALL_RUN_CREATORS,
  type RunCreatorFilter,
} from "../components/RunCreatorSelect";
import RunStatusSelect, { type RunFilter } from "../components/RunStatusSelect";
import AgentLaunchPage from "./AgentLaunchPage";

export default function AgentRunsPage() {
  const auth = useAuth();
  const workspaceSession = useWorkspaceSession();
  const [filter, setFilter] = useState<RunFilter>("all");
  const [creator, setCreator] = useState<RunCreatorFilter>(ALL_RUN_CREATORS);
  const [ticker, setTicker] = useState("");
  const [page, setPage] = useState(1);
  const runs = useAgentRunPage({
    createdBy: creator === ALL_RUN_CREATORS ? undefined : creator,
    status: filter === "all" ? undefined : filter,
    tickerSearch: ticker,
    offset: (page - 1) * AGENT_RUN_PAGE_SIZE,
  });
  const [creatorCatalog, setCreatorCatalog] = useState<readonly AgentRun[]>([]);
  const [launching, setLaunching] = useState(false);
  useEffect(() => {
    if (
      creator === ALL_RUN_CREATORS &&
      filter === "all" &&
      ticker.trim() === "" &&
      page === 1 &&
      runs.data
    ) {
      setCreatorCatalog(runs.data.items);
    }
  }, [creator, filter, page, runs.data, ticker]);

  const creatorRuns =
    creator === ALL_RUN_CREATORS
      ? (runs.data?.items ?? creatorCatalog)
      : creatorCatalog;
  const currentSubject =
    workspaceSession.data?.identity.subject ?? auth.identity?.subject ?? null;
  const total = runs.data?.total ?? 0;
  const pageSize = runs.data?.limit ?? AGENT_RUN_PAGE_SIZE;
  const currentPage = Math.min(page, pageCount(total, pageSize));
  const visibleRuns = runs.data?.items ?? [];
  useEffect(() => {
    if (runs.data && currentPage !== page) setPage(currentPage);
  }, [currentPage, page, runs.data]);

  return (
    <div className="page page--workspace page--fixed-catalog-header">
      <PageHeader className="run-toolbar" aria-label="Run catalog controls">
        <h1>Runs</h1>
        <RunStatusSelect
          value={filter}
          onChange={(value) => {
            setFilter(value);
            setPage(1);
          }}
        />
        <RunCreatorSelect
          runs={creatorRuns}
          currentSubject={currentSubject}
          value={creator}
          onChange={(value) => {
            setCreator(value);
            setPage(1);
          }}
        />
        <Search
          value={ticker}
          onValueChange={(value) => {
            setTicker(value);
            setPage(1);
          }}
          placeholder="Filter by ticker"
          aria-label="Filter by ticker"
          className="run-toolbar__search"
        />
        <Button
          tone="primary"
          aria-label="Launch a new run"
          iconLeft={<PlusIcon />}
          onClick={() => setLaunching(true)}
        >
          New run
        </Button>
      </PageHeader>

      <div className="fixed-catalog-scroll">
        {runs.error && runs.data && (
          <InlineNotice tone="warning">
            Live updates are paused. Showing the latest cached run state.
          </InlineNotice>
        )}
        {runs.isPending && !runs.data ? (
          <Loading label="Loading E2E runs…" />
        ) : runs.error && !runs.data ? (
          <ErrorState error={runs.error} onRetry={() => runs.refetch()} />
        ) : visibleRuns.length === 0 ? (
          <Empty>No runs match these filters.</Empty>
        ) : (
          <>
            <AgentRunTable
              runs={visibleRuns}
              showVersion={auth.canOperate}
              showModelColumns={!hideRunModelColumns()}
            />
            <Pagination
              page={currentPage}
              pageSize={pageSize}
              total={total}
              ariaLabel="Run pages"
              onPageChange={setPage}
            />
          </>
        )}
      </div>
      {launching && (
        <Modal
          size="large"
          className="agent-launch-modal"
          ariaLabel="Launch a new run"
          onClose={() => setLaunching(false)}
        >
          <ModalCloseButton floating onClick={() => setLaunching(false)} />
          <AgentLaunchPage modal onClose={() => setLaunching(false)} />
        </Modal>
      )}
    </div>
  );
}
